import { useState, useEffect, useMemo } from 'react';
import { authService } from '../services/api';

// Module-level cache shared across all hook instances: botId → url | null
const _cache = new Map();
// botId → Promise<url | null> enquanto a busca está em andamento
const _inflight = new Map();
// Lote aberto: ids pedidos no mesmo tick viram UMA chamada ao backend.
let _batch = null;
// Espelha _PHOTOS_BATCH_MAX do backend.
const BATCH_MAX = 100;

async function fetchBatch(ids) {
  const photos = {};
  for (let i = 0; i < ids.length; i += BATCH_MAX) {
    const chunk = ids.slice(i, i + BATCH_MAX);
    try {
      const { data } = await authService.bots.get_bot_photos(chunk);
      Object.assign(photos, data?.photos || {});
    } catch {
      // Sem foto para o lote que falhou: mesmo fallback da busca individual.
    }
  }
  return photos;
}

/**
 * Pede a foto de um bot. Várias instâncias do hook montando juntas (seletor
 * de bots + página) antes disparavam uma requisição POR BOT cada; agora
 * todos os pedidos do mesmo tick vão num lote só e pedidos repetidos
 * reaproveitam a busca em andamento.
 */
function requestPhoto(botId) {
  if (_inflight.has(botId)) return _inflight.get(botId);
  if (!_batch) {
    const batch = { ids: [] };
    batch.promise = new Promise(resolve => setTimeout(resolve, 0)).then(() => {
      _batch = null;
      return fetchBatch(batch.ids);
    });
    _batch = batch;
  }
  _batch.ids.push(botId);
  const p = _batch.promise.then(photos => {
    const url = photos[String(botId)] || null;
    _cache.set(botId, url);
    _inflight.delete(botId);
    return url;
  });
  _inflight.set(botId, p);
  return p;
}

/**
 * Fetches and caches photo URLs for a list of bots.
 * Returns a plain object { [botId]: url | null }.
 * Only bots with bot_photo_file_id are fetched.
 * Subsequent calls with the same bot IDs hit the cache instantly.
 */
export function useBotPhotos(bots = []) {
  const [photoUrls, setPhotoUrls] = useState(() => {
    const initial = {};
    for (const bot of bots) {
      if (_cache.has(bot.id)) initial[bot.id] = _cache.get(bot.id);
    }
    return initial;
  });

  const botKey = useMemo(
    () => bots.map(b => b.id).join(','),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bots]
  );

  useEffect(() => {
    if (!bots || bots.length === 0) return;

    // Hydrate state from cache first — covers the case where another component
    // already fetched these photos (e.g. BotSelector) but this component
    // mounted with an empty list (e.g. ManageBotsModal) and missed the init.
    const cached = {};
    for (const bot of bots) {
      if (_cache.has(bot.id)) cached[bot.id] = _cache.get(bot.id);
    }
    if (Object.keys(cached).length > 0) {
      setPhotoUrls(prev => ({ ...cached, ...prev }));
    }

    const toFetch = bots.filter(b => b.bot_photo_file_id && !_cache.has(b.id));
    if (toFetch.length === 0) return;

    Promise.all(
      toFetch.map(bot => requestPhoto(bot.id).then(url => ({ id: bot.id, url })))
    ).then(results => {
      const updates = {};
      for (const { id, url } of results) updates[id] = url;
      setPhotoUrls(prev => ({ ...prev, ...updates }));
    });
  // botKey is the stable dep — reruns only when bot IDs change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [botKey]);

  return photoUrls;
}

/** Invalidates the cache for a specific botId (call after photo update) */
export function invalidateBotPhoto(botId) {
  _cache.delete(botId);
}

/**
 * Builds a { [bot_username]: photoUrl } map for quick lookup by username.
 * Composes useBotPhotos internally — no extra fetches if photos are already cached.
 */
export function useBotPhotosByUsername(bots = []) {
  const photoUrls = useBotPhotos(bots);
  return useMemo(() => {
    const m = {};
    for (const bot of bots) {
      if (photoUrls[bot.id]) m[bot.bot_username] = photoUrls[bot.id];
    }
    return m;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bots, photoUrls]);
}
